import React from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Separator } from '@/components/ui/Separator';
import { Clock, AlertCircle, UserMinus } from 'lucide-react';
import type { ScheduleOccurrence } from '../../hooks/useSchedule';
import { getOccurrenceScopeLabel } from '../../utils/bookingScopeLabel';
import { isEligibleForMakeupCredit } from '@court-sync/shared';

interface LessonDatePopoverProps {
  occurrence: ScheduleOccurrence;
  onReportAbsence?: (occurrence: ScheduleOccurrence) => void;
  onRequestRefund?: (occurrence: ScheduleOccurrence) => void;
  /** Base lessons path for rebook (e.g. /academy/:slug/lessons) */
  lessonsPath?: string;
  /** Academy owner: open remove-from-session/term flow */
  onRemoveFromLesson?: (occurrence: ScheduleOccurrence) => void;
}

export function LessonDatePopover({
  occurrence,
  onReportAbsence,
  onRequestRefund,
  lessonsPath,
  onRemoveFromLesson,
}: LessonDatePopoverProps) {
  const now = new Date();
  const lessonStart = new Date(occurrence.date);
  const [hours, minutes] = occurrence.startTime.split(':').map(Number);
  lessonStart.setHours(hours, minutes, 0, 0);

  const isPast = lessonStart < now;
  const canReportAbsence =
    !isPast &&
    occurrence.makeupLessonEnabled &&
    occurrence.status === 'scheduled' &&
    isEligibleForMakeupCredit(
      now,
      lessonStart,
      occurrence.makeupNotificationDeadlineHours
    );

  const getStatusBadge = () => {
    if (occurrence.status === 'cancelled') {
      return (
        <Badge variant="destructive" className="text-xs">
          Cancelled
          {occurrence.override?.reason && `: ${occurrence.override.reason}`}
        </Badge>
      );
    }
    if (occurrence.status === 'postponed') {
      const newDate = occurrence.override?.newDate;
      return (
        <Badge variant="warning" className="text-xs">
          Postponed
          {newDate && ` → ${format(new Date(newDate), 'MMM d')}`}
        </Badge>
      );
    }
    if (isPast) {
      return (
        <Badge variant="secondary" className="text-xs">
          Past
        </Badge>
      );
    }
    return (
      <Badge variant="success" className="text-xs">
        Scheduled
      </Badge>
    );
  };

  const canRemoveFromLesson =
    !!onRemoveFromLesson &&
    occurrence.status === 'scheduled' &&
    occurrence.bookingScope != null &&
    (occurrence.lessonType === 'group' || occurrence.lessonType === 'camp');

  const scopeLabel = getOccurrenceScopeLabel({
    bookingScope: occurrence.bookingScope,
    lessonType: occurrence.lessonType,
  });

  return (
    <div className="space-y-3">
      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white">
          {occurrence.lessonName}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {format(occurrence.date, 'EEEE, MMMM d')}
        </p>
        {getStatusBadge()}
      </div>

      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
        <Clock className="h-4 w-4 flex-shrink-0" />
        <span>
          {occurrence.startTime} – {occurrence.endTime}
        </span>
      </div>

      {occurrence.courtLabel && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Court: {occurrence.courtLabel}
        </p>
      )}

      <div className="flex flex-wrap gap-1">
        {scopeLabel && (
          <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            {scopeLabel}
          </Badge>
        )}
        <Badge variant="outline" className="text-xs">
          {occurrence.lessonType}
        </Badge>
        {occurrence.skillLevel && (
          <Badge variant="outline" className="text-xs capitalize">
            {occurrence.skillLevel}
          </Badge>
        )}
      </div>

      {onReportAbsence &&
        occurrence.makeupLessonEnabled &&
        occurrence.status === 'scheduled' &&
        !isPast && (
        <>
          <Separator />
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Report absence at least{' '}
            {occurrence.makeupNotificationDeadlineHours}h before the lesson to
            earn a make-up credit.
          </p>
        </>
      )}

      {canReportAbsence && onReportAbsence && (
        <Button
          size="sm"
          className="w-full"
          onClick={() => onReportAbsence(occurrence)}
        >
          Report Absence
        </Button>
      )}

      {canRemoveFromLesson && (
        <>
          <Separator />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="w-full gap-1.5 text-red-700 dark:text-red-300 border-red-200 dark:border-red-900/50"
            onClick={() => onRemoveFromLesson(occurrence)}
          >
            <UserMinus className="h-4 w-4 shrink-0" aria-hidden />
            Remove from lesson
          </Button>
        </>
      )}

      {!canReportAbsence &&
        occurrence.makeupLessonEnabled &&
        occurrence.status === 'scheduled' &&
        !isPast &&
        onReportAbsence && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 p-2 text-xs text-amber-800 dark:text-amber-200">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Deadline passed. Report absences at least{' '}
              {occurrence.makeupNotificationDeadlineHours} hours before the
              lesson.
            </span>
          </div>
        )}

      {occurrence.status === 'cancelled' && !occurrence.isRefunded && (lessonsPath || onRequestRefund) && (
        <>
          <Separator />
          <div className="flex flex-wrap gap-2">
            {lessonsPath && (
              <Link
                to={`${lessonsPath}?rebookFrom=${occurrence.bookingId}&lessonType=${occurrence.lessonType}${occurrence.skillLevel ? `&skillLevel=${occurrence.skillLevel}` : ''}`}
              >
                <Button size="sm" variant="default">
                  Pick Another Time
                </Button>
              </Link>
            )}
            {onRequestRefund && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRequestRefund(occurrence)}
              >
                Request Refund
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
